import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useMatch, useParams} from 'react-router';

import {skipToken} from '@reduxjs/toolkit/query';

import {
  Button,
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalHeader,
  ModalSubmitButton,
  NewSelectField,
  showModal,
  showNewNotification,
} from '@edna/components';
import {Label} from '@edna/components/FormField';
import {Stack} from '@edna/components/primitives';
import {useOnChange} from '@edna/hooks';
import {ECurrencySymbol} from '@edna/utils';

import {FieldsRow, Text} from 'src/components/primitives';
import {CHANNEL_LABEL, EChannelType} from 'src/constants';
import {
  useGetLeadClientQuery,
  useGetTenantLeadDetailsQuery,
} from 'src/containers/LeadCompanyClients/api';
import {ELicensePeriod} from 'src/containers/Licenses/definitions';
import {useGetSupportClientQuery} from 'src/containers/SupportClients/api';
import {SettingsIcon} from 'src/icons';
import PATHS from 'src/paths';
import {joiResolver} from 'src/utils';

import {useCreateAvailableTariffMutation, useGetTariffsLicensesQuery} from '../api';
import {TCreateAvailableTariffFormData} from '../definitions';
import {useLicenseInfo} from '../useLicenseInfo';
import {COLUMNS_WIDTH, WHATSAPP_COLUMNS_WIDTH} from '../utils';
import {CoefficientsForm} from './CoefficientsForm';
import * as S from './style';

const coefficientsResolver = (channelType: EChannelType) => {
  if (channelType === EChannelType.WHATSAPP) {
    return joiResolver((joi) =>
      joi.object({
        license: joi.number().required(),
        connectionFee: joi.number().required(),
        amau: joi.number().required(),
      }),
    );
  }

  return joiResolver((joi) =>
    joi.object({
      license: joi.number().required(),
    }),
  );
};

const CoefficientsModal = React.memo<{
  closeModal: TEmptyFunction;
  formValues: TCreateAvailableTariffFormData;
  onSubmit?: (tariff: TCreateAvailableTariffFormData) => void;
}>(({closeModal, formValues, onSubmit}) => {
  const {t} = useTranslation();
  const isLeadClientView = !!useMatch(`${PATHS.COMPANY_CLIENTS}/*`);

  const [createTariff, {isLoading}] = useCreateAvailableTariffMutation();

  const methods = useForm<TCreateAvailableTariffFormData>({
    mode: 'all',
    defaultValues: formValues,
    resolver: coefficientsResolver(formValues.channelType),
  });

  useOnChange(() => methods.reset(formValues), [formValues]);

  const {license} = useGetTariffsLicensesQuery(
    {
      channelType: formValues.channelType,
      licensePeriod: formValues.licensePeriod,
    },
    {
      selectFromResult: ({data}) => ({
        license: data?.find((item) => item.id === formValues.licenseDetailId),
      }),
    },
  );

  const handleSubmit = React.useCallback(
    async (values: TCreateAvailableTariffFormData) => {
      if (!license) {
        return;
      }

      if (onSubmit) {
        onSubmit(values);
        closeModal();

        return;
      }

      try {
        const createdTariff = await createTariff(values).unwrap();

        closeModal();
        showNewNotification({
          type: 'SUCCESS',
          message: t('Tariffs:successNotifications.createTariff', {name: createdTariff.name}),
        });
      } catch (e) {}
    },
    [closeModal, license],
  );

  return (
    <Modal width="830px">
      <FormProvider {...methods}>
        <ModalHeader>
          {t('Tariffs:editTariffModal.modifyTitle', {name: license?.label})}
        </ModalHeader>
        <Stack size="4">
          <Text secondary size="S">
            {t(`Tariffs:editTariffModal.${isLeadClientView ? 'modifyHint' : 'coefficientsHint'}`)}
          </Text>
          <CoefficientsForm channelType={formValues.channelType} license={license} />
        </Stack>
        <ModalActions>
          <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
          <ModalSubmitButton onClick={methods.handleSubmit(handleSubmit)} disabled={isLoading}>
            {t('Tariffs:createTariffModal.submitButton')}
          </ModalSubmitButton>
        </ModalActions>
      </FormProvider>
    </Modal>
  );
});

CoefficientsModal.displayName = 'CoefficientsModal';

const showCoefficientsModal = (
  formValues: TCreateAvailableTariffFormData,
  onSubmit?: (tariff: TCreateAvailableTariffFormData) => void,
) =>
  showModal((closeModal) => (
    <CoefficientsModal closeModal={closeModal} formValues={formValues} onSubmit={onSubmit} />
  ));

type TProps = {
  channelType: EChannelType;
  licensePeriod?: ELicensePeriod;
  onSubmit?: (tariff: TCreateAvailableTariffFormData) => void;
};

const CreateTariffModal = React.memo<
  TProps & {
    closeModal: TEmptyFunction;
  }
>(
  ({
    closeModal,
    channelType,
    onSubmit,
    licensePeriod: licensePeriodProp = ELicensePeriod.MONTH,
  }) => {
    const {t} = useTranslation();
    const {id} = useParams();
    const isLeadClientView = !!useMatch(`${PATHS.COMPANY_CLIENTS}/*`);

    const isWhatsApp = channelType === EChannelType.WHATSAPP;

    const defaultValues = React.useMemo(() => {
      const values: Partial<TCreateAvailableTariffFormData> = {
        channelType,
        licensePeriod: licensePeriodProp,
        license: 1,
      };

      if (channelType === EChannelType.WHATSAPP) {
        values.connectionFee = 1;
        values.amau = 1;
      }

      return values;
    }, [channelType, licensePeriodProp]);

    const methods = useForm<TCreateAvailableTariffFormData>({
      mode: 'all',
      defaultValues,
      resolver: joiResolver((joi) =>
        joi.object({
          licenseDetailId: joi.number().required(),
        }),
      ),
    });

    useOnChange(() => methods.reset(defaultValues), [defaultValues]);

    const licensePeriod = methods.watch('licensePeriod') as ELicensePeriod;
    const licenseDetailId = methods.watch('licenseDetailId');

    useOnChange(() => methods.resetField('licenseDetailId'), [licensePeriod]);

    const {data: leadClient} = useGetLeadClientQuery(id ?? skipToken, {skip: !isLeadClientView});
    const {data: supportClientLead} = useGetTenantLeadDetailsQuery(id ?? skipToken, {
      skip: isLeadClientView,
    });
    const {data: supportClient} = useGetSupportClientQuery(id ?? skipToken, {
      skip: isLeadClientView,
    });
    const {data: licenses = []} = useGetTariffsLicensesQuery(
      {
        channelType,
        licensePeriod,
        currency: supportClient?.defaultCurrency ?? leadClient?.currencyCode,
        resellerId: supportClient?.resellerId ?? leadClient?.resellerId,
        documentType: supportClientLead?.documentType ?? leadClient?.documentType,
      },
      {skip: isLeadClientView ? !leadClient : !supportClient || !supportClientLead},
    );
    const [createTariff, {isLoading}] = useCreateAvailableTariffMutation();

    const license = React.useMemo(
      () => licenses?.find((item) => item.id === licenseDetailId),
      [licenses, licenseDetailId],
    );

    const licenseInfo = useLicenseInfo({license});

    const currency = React.useMemo(() => {
      const currencyValue = supportClient?.defaultCurrency ?? leadClient?.currencyCode;

      return currencyValue ? ECurrencySymbol[currencyValue] : null;
    }, [supportClient, leadClient]);

    const handleChangeCoefficients = React.useCallback(
      (values: TCreateAvailableTariffFormData) => {
        if (!license) {
          return;
        }

        const tariffData = {
          ...values,
          name: `${license.catalogTariffName} ${license.label}`,
          tenantId: isLeadClientView ? leadClient?.reservedTenantId : Number(id),
          tariffCatalogId: license.catalogTariffId,
        };

        showCoefficientsModal(tariffData, onSubmit);
        closeModal();
      },
      [closeModal, onSubmit, license, leadClient, id, isLeadClientView],
    );

    const handleSubmit = React.useCallback(
      async (values: TCreateAvailableTariffFormData) => {
        if (!license) {
          return;
        }

        const tariffData = {
          ...values,
          tenantId: isLeadClientView ? leadClient?.reservedTenantId : Number(id),
          name: `${license.catalogTariffName} ${license.label}`,
          tariffCatalogId: license.catalogTariffId,
        };

        if (onSubmit) {
          onSubmit(tariffData);
          closeModal();

          return;
        }

        try {
          const createdTariff = await createTariff(tariffData).unwrap();

          closeModal();
          showNewNotification({
            type: 'SUCCESS',
            message: t('Tariffs:successNotifications.createTariff', {name: createdTariff.name}),
          });
        } catch (e) {}
      },
      [closeModal, onSubmit, license, id, isLeadClientView, leadClient, t],
    );

    return (
      <Modal width="830px">
        <FormProvider {...methods}>
          <ModalHeader>
            {t('Tariffs:createTariffModal.title', {channelType: CHANNEL_LABEL[channelType]})}
          </ModalHeader>
          <Stack size="4">
            <Text>{t('Tariffs:createTariffModal.parameters')}</Text>
            {isWhatsApp && (
              <Stack isFullWidth size="2">
                <Label>{t('Tariffs:createTariffModal.period')}</Label>
                <Stack direction="ROW" isFullWidth size="6">
                  <S.RadioField
                    name="licensePeriod"
                    type="radio"
                    value={ELicensePeriod.MONTH}
                    isGroupViewType
                    noMargin
                  >
                    {t(`Tariffs:licensePeriod.${ELicensePeriod.MONTH}`)}
                  </S.RadioField>
                  <S.RadioField
                    name="licensePeriod"
                    type="radio"
                    value={ELicensePeriod.YEAR}
                    isGroupViewType
                    noMargin
                  >
                    {t(`Tariffs:licensePeriod.${ELicensePeriod.YEAR}`)}
                  </S.RadioField>
                </Stack>
              </Stack>
            )}
            <NewSelectField
              label={t('Tariffs:createTariffModal.selectBaseTariff')}
              name="licenseDetailId"
              noMargin
              options={licenses.map((item) => ({
                label: `${item.catalogTariffName} ${item.label}`,
                value: item.id,
              }))}
            />
            {isWhatsApp && (
              <FieldsRow size="5" columnsWidth={WHATSAPP_COLUMNS_WIDTH}>
                <S.Label>{t('Tariffs:createTariffModal.mauCount')}</S.Label>
                <S.NumberInput value={licenseInfo.mauCount ?? 0} readOnly />
                <S.Label>{t('Tariffs:createTariffModal.licensePrice', {currency})}</S.Label>
                <S.NumberInput value={licenseInfo.licensePrice} readOnly />
                <S.Label>{t('Tariffs:createTariffModal.connectionFee', {currency})}</S.Label>
                <S.NumberInput value={licenseInfo.connectionFee} readOnly />
                <S.Label>{t('Tariffs:createTariffModal.amauPrice', {currency})}</S.Label>
                <S.NumberInput value={licenseInfo.amauPrice} readOnly />
              </FieldsRow>
            )}
            {!isWhatsApp && (
              <FieldsRow size="5" columnsWidth={COLUMNS_WIDTH}>
                <S.Label>{t('Tariffs:createTariffModal.monthLicensePrice', {currency})}</S.Label>
                <S.NumberInput value={licenseInfo.licensePrice} readOnly />
              </FieldsRow>
            )}
            <Text secondary size="XS">
              {t('Tariffs:pricesIsWithoutVat')}
            </Text>
            <Button size="S" onClick={methods.handleSubmit(handleChangeCoefficients)}>
              <S.LeftIcon as={SettingsIcon} />
              {t('Tariffs:createTariffModal.modifyTariffButton')}
            </Button>
          </Stack>
          <ModalActions>
            <ModalCancelButton onClick={closeModal}>{t('Common:button.cancel2')}</ModalCancelButton>
            <ModalSubmitButton
              onClick={methods.handleSubmit(handleSubmit)}
              disabled={isLoading}
              loading={isLoading}
            >
              {t('Tariffs:createTariffModal.submitButton')}
            </ModalSubmitButton>
          </ModalActions>
        </FormProvider>
      </Modal>
    );
  },
);

CreateTariffModal.displayName = 'CreateTariffModal';

export const showCreateTariffModal = ({channelType, licensePeriod, onSubmit}: TProps) =>
  showModal((closeModal) => (
    <CreateTariffModal
      closeModal={closeModal}
      channelType={channelType}
      licensePeriod={licensePeriod}
      onSubmit={onSubmit}
    />
  ));
